import { z } from 'zod'

const ItemOperationNames = ['READ', 'WRITE', 'DELETE'] as const
export type ItemOperation = (typeof ItemOperationNames)[number]

const PrincipalTypeNames = ['PUBLIC', 'AUTHENTICATED'] as const

export enum PrincipalType {
  PUBLIC = 'PUBLIC',
  AUTHENTICATED = 'AUTHENTICATED',
}

export type Principal = string | PrincipalType

export const aclOrigins = ['own', 'inherited'] as const
export type ACLOrigin = (typeof aclOrigins)[number]

/**
 * Deliberately lenient — this schema is embedded in `persistedSchema` and
 * `accessControlSchema`, so it runs on every entity READ. Refining it here
 * would make an already-stored `PUBLIC` + `WRITE` row unreadable (and would
 * make `aclAuthorize` deny the entity's own owner, since it fails closed on a
 * parse error). Use `grantableAclSchema` on write paths instead.
 */
export const aclSchema = z.object({
  principal: z.union([z.string(), z.enum(PrincipalTypeNames)]),
  permission: z.enum(ItemOperationNames),
  /**
   * Optional grant expiry. Evaluated in `hasACLPermission`, never here — a refine on
   * this schema would run on every entity READ and fail closed.
   *
   * Optional rather than defaulted so every ACL already in DynamoDB still parses. It is
   * declared ahead of its first writer on purpose: `share.ts` persists the *parsed*
   * output of `grantableAclSchema`, and `z.object` strips unknown keys, so a caller
   * sending an undeclared `expiresAt` would get a 200 and silently lose it — a grant
   * that should have stopped and did not.
   */
  expiresAt: z.string().describe('ISO8601 instant after which this grant no longer applies').optional(),
  /**
   * Where the grant came from: made on this item (`own`) or copied from its parent (`inherited`). A share keeps an
   * item's own grants and replaces its inherited ones (GH #2277). Unset on every grant written before this existed;
   * a share or move classifies those once and writes the result. Server-owned: the share endpoint derives it.
   */
  origin: z
    .enum(aclOrigins)
    .describe("'own' if granted on this item, 'inherited' if copied from its parent")
    .optional(),
})
export type ACL = z.infer<typeof aclSchema>

/**
 * `PUBLIC` is the only principal an unauthenticated caller can match, so it may
 * only ever grant READ. `AUTHENTICATED` ("any logged-in user") keeps every
 * permission — that combination is legitimate.
 */
export const isGrantableACL = (acl: ACL) => acl.principal !== PrincipalType.PUBLIC || acl.permission === 'READ'

/** Strict ACL schema for write paths: rejects grants that PUBLIC must never confer. */
export const grantableAclSchema = aclSchema.refine(isGrantableACL, {
  message: 'The PUBLIC principal may only be granted READ permission',
  path: ['permission'],
})

export function isACL(val: unknown): val is ACL {
  return aclSchema.safeParse(val).success
}
export function isACLs(val: unknown): val is ACL[] {
  return z.array(aclSchema).safeParse(val).success
}
